import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../styles/SellerForm.css';

const EditSellerForm = ({ seller, onSellerUpdated, onCancel }) => {
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    user_type: '',
    supervisor_scope: '',
    pos_id: '',
    password: '' // Campo opcional para cambiar la contraseña
  });
  
  const [pointsOfSale, setPointsOfSale] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [changePassword, setChangePassword] = useState(false);

  // Inicializar el formulario con los datos del vendedor
  useEffect(() => {
    if (seller) {
      setFormData({
        email: seller.email || '',
        full_name: seller.full_name || '',
        user_type: seller.role === 'manager' ? 'supervisor' : 'seller',
        supervisor_scope: seller.role === 'manager' ? 'general' : '',
        pos_id: seller.pos_id || '',
        password: ''
      });
    }
  }, [seller]);

  // Fetch points of sale for the dropdown
  useEffect(() => {
    const fetchPointsOfSale = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        const response = await axios.get('/api/points-of-sale/all', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        // Filter only active (not closed) points of sale
        const activePointsOfSale = response.data.filter(pos => !pos.pos_closed);
        setPointsOfSale(activePointsOfSale);
      } catch (error) {
        console.error('Error fetching points of sale:', error);
        setError('No se pudieron cargar los puntos de venta. Por favor, inténtelo de nuevo.');
      } finally {
        setLoading(false);
      }
    };

    fetchPointsOfSale();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updatedFormData = {
      ...formData,
      [name]: value
    };
    
    // Auto-select "Supervisor General" when user type is "supervisor"
    if (name === 'user_type' && value === 'supervisor') {
      updatedFormData.supervisor_scope = 'general';
    }
    
    setFormData(updatedFormData);
  };

  const handlePasswordCheckboxChange = (e) => {
    setChangePassword(e.target.checked);
    if (!e.target.checked) {
      // Si se desmarca, limpiar el campo de contraseña
      setFormData({
        ...formData,
        password: ''
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.full_name || !formData.user_type) {
      setError('Por favor, complete todos los campos requeridos.');
      return;
    }
    
    // Validaciones específicas por tipo de usuario
    if (formData.user_type === 'seller' && !formData.pos_id) {
      setError('Por favor, seleccione un punto de venta para el vendedor.');
      return;
    }
    
    // Si se marcó cambiar contraseña pero no se proporcionó una
    if (changePassword && !formData.password) {
      setError('Por favor, ingrese la nueva contraseña.');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      
      // Preparar datos para la actualización
      const updateData = {
        email: formData.email,
        full_name: formData.full_name,
        user_type: formData.user_type,
        pos_id: formData.user_type === 'seller' ? formData.pos_id : null
      };
      
      // Solo incluir la contraseña si se va a cambiar
      if (changePassword && formData.password) {
        updateData.password = formData.password;
      }
      
      // Actualizar el vendedor
      await axios.put(`/api/sellers/seller/${seller.id}`, 
        updateData,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      setSuccess(true);
      
      // Notificar al componente padre
      if (onSellerUpdated) {
        onSellerUpdated();
      }
      
      // Reset success message after 3 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
      
    } catch (error) {
      console.error('Error updating seller:', error);
      setError(error.response?.data?.message || 'Error al actualizar el vendedor. Por favor, inténtelo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="seller-form-container">
      <h3>Editar {formData.user_type === 'supervisor' ? 'Supervisor' : 'Vendedor'}</h3>
      
      {success && (
        <div className="success-message">
          ¡Vendedor actualizado exitosamente!
        </div>
      )}
      
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="seller-form">
        <div className="form-group">
          <label htmlFor="user_type">Tipo de Usuario</label>
          <select
            id="user_type"
            name="user_type"
            value={formData.user_type}
            onChange={handleChange}
            required
          >
            <option value="">Seleccionar tipo de usuario</option>
            <option value="seller">Vendedor</option>
            <option value="supervisor">Supervisor</option>
          </select>
        </div>
        
        <div className="form-group">
          <label htmlFor="full_name">Nombre Completo</label>
          <input
            type="text"
            id="full_name"
            name="full_name"
            value={formData.full_name}
            onChange={handleChange}
            placeholder={`Nombre completo del ${formData.user_type === 'supervisor' ? 'supervisor' : 'vendedor'}`}
            required
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="email">Correo Electrónico</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="correo@ejemplo.com"
            required
          />
        </div>
        
        <div className="form-group password-change-group">
          <div className="password-checkbox">
            <input
              type="checkbox"
              id="change_password"
              name="change_password"
              checked={changePassword}
              onChange={handlePasswordCheckboxChange}
            />
            <label htmlFor="change_password">Cambiar contraseña</label>
          </div>
          
          {changePassword && (
            <div className="password-input">
              <label htmlFor="password">Nueva Contraseña</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Nueva contraseña"
                required={changePassword}
              />
            </div>
          )}
        </div>
        
        {formData.user_type === 'supervisor' && (
          <div className="form-group">
            <label htmlFor="supervisor_scope">Alcance del Supervisor</label>
            <select
              id="supervisor_scope"
              name="supervisor_scope"
              value={formData.supervisor_scope}
              onChange={handleChange}
              disabled={true}
              required
            >
              <option value="general">Supervisor General (Todo el Negocio)</option>
            </select>
          </div>
        )}
        
        {formData.user_type === 'seller' && (
          <div className="form-group">
            <label htmlFor="pos_id">Punto de Venta</label>
            <select
              id="pos_id"
              name="pos_id"
              value={formData.pos_id}
              onChange={handleChange}
              required
            >
              <option value="">Seleccionar punto de venta</option>
              {pointsOfSale.map(pos => (
                <option key={pos.id} value={pos.id}>
                  {pos.pos_name}
                </option>
              ))}
            </select>
          </div>
        )}
        
        <div className="form-actions">
          <button 
            type="button" 
            onClick={onCancel} 
            className="cancel-btn"
          >
            Cancelar
          </button>
          <button 
            type="submit" 
            className="submit-btn" 
            disabled={loading}
          >
            {loading ? 'Actualizando...' : `Actualizar ${formData.user_type === 'supervisor' ? 'Supervisor' : 'Vendedor'}`}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditSellerForm;
